import React from "react";
import { AlertCircle, PackageSearch } from "lucide-react";
import ItemCard from "./ItemCard";
import BrowseView from "./BrowseView";
import useOnlineOrderingBrowse from "../../hooks/useOnlineOrderingBrowse";
import { MAX_RESULTS, MIN_QUERY_LENGTH } from "../../hooks/useItemSearch";

const GRID = "grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5";

function Skeletons() {
  return (
    <div className={GRID} aria-hidden="true">
      {Array.from({ length: MAX_RESULTS }).map((_, i) => (
        <div key={i} className="animate-pulse overflow-hidden rounded-xl border border-gray-100 bg-white">
          <div className="aspect-square bg-gray-100" />
          <div className="space-y-2 p-3">
            <div className="h-3 w-5/6 rounded bg-gray-100" />
            <div className="h-3 w-1/2 rounded bg-gray-100" />
            <div className="h-7 w-full rounded bg-gray-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function Message({ icon: Icon, title, children }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white px-6 py-16 text-center">
      <Icon className="mb-3 h-10 w-10 text-gray-300" aria-hidden="true" />
      <p className="font-medium text-gray-900">{title}</p>
      {children && <div className="mt-1 text-sm text-gray-500">{children}</div>}
    </div>
  );
}

// Fetches the browse tree (favourites + menu sections + categories) once
// per customerId and hands it to BrowseView. Kept as its own component
// (rather than inline in ItemGrid) so its hook only runs while the browse
// view is actually shown, not on every ItemGrid render.
function ItemGridBrowse({ customerId, isFavourite, onToggleFavourite }) {
  const { favourites, menuSections, categories, loading, error } = useOnlineOrderingBrowse(customerId);

  if (loading) return <Skeletons />;

  return (
    <BrowseView
      favourites={favourites}
      menuSections={menuSections}
      categories={categories}
      loading={loading}
      error={error}
      isFavourite={isFavourite}
      onToggleFavourite={onToggleFavourite}
      customerId={customerId}
    />
  );
}

export default function ItemGrid({
  query,
  items,
  loading,
  error,
  onRetry,
  isFavourite,
  onToggleFavourite,
  customerId,
}) {
  const trimmed = query.trim();

  // With no search typed, show the full browse view instead of an empty
  // page: Favourites, then "From our menu" (AI Menu sections), then
  // Inventory (major_category groups) — see BrowseView / useOnlineOrderingBrowse.
  if (trimmed.length < MIN_QUERY_LENGTH) {
    return <ItemGridBrowse customerId={customerId} isFavourite={isFavourite} onToggleFavourite={onToggleFavourite} />;
  }
  if (loading) return <Skeletons />;
  if (error) {
    return (
      <Message icon={AlertCircle} title="Something went wrong">
        <p>{error}</p>
        <button type="button" onClick={onRetry} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
          Try again
        </button>
      </Message>
    );
  }
  if (items.length === 0) {
    return (
      <Message icon={PackageSearch} title={`No items match “${trimmed}”`}>
        Check the spelling or try a shorter word.
      </Message>
    );
  }

  return (
    <div>
      <p className="mb-3 text-sm text-gray-500">
        Showing {items.length} {items.length === 1 ? "item" : "items"} for “{trimmed}”
        {items.length === MAX_RESULTS && " · refine your search to see others"}
      </p>
      <div className={GRID}>
        {items.map((item) => {
          // AI Menu items (source: "ai_menu") carry the merchant's own
          // isFavourite flag and aren't toggleable by the shopper — same as
          // the "From our menu" section, so no heart *button* on these
          // cards, but the heart still shows (non-interactive) when the
          // merchant favourited it.
          const isMenuItem = item.source === "ai_menu";
          const favourited = isMenuItem
            ? item.is_favourite === true
            : Boolean(isFavourite && isFavourite(item));
          return (
            <ItemCard
              key={item.upc || item.id}
              item={item}
              isFavourite={favourited}
              onToggleFavourite={isMenuItem ? undefined : onToggleFavourite}
            />
          );
        })}
      </div>
    </div>
  );
}
